import { push } from "connected-react-router";
import { length, splitAt, zip } from "ramda";
import { Action, ActionFunction1 } from "redux-actions";
import { call, put, select } from "redux-saga/effects";

import { Creators } from "../../../actions/common";
import { fetchAndResolveCourses } from "../../../actions/courses";
import { SagaFunction } from "../../../actions/types";
import { AxiosInterceptedResult } from "../../../api/interceptors";
import { IResolveResponse, ResolvableType } from "../../../api/ResolveApi/resolve-api.types";
import { IClientPatch, IPatchAction, IPatchPayload } from "../../../components/course-author/helpers/patches";
import {
  modifyObjectDeletedPatch,
  patchPayloadCreator,
} from "../../../components/course-author/helpers/patches/creators";
import { dependencies } from "../../../dependencies";
import {
  IClassResponse,
  IUsersUIData,
  resolveClassPostSuccessResponseValueToClassUIData as mapDataToClass,
  resolveCoursePostSuccessResponseValueToCourseUIData as mapDataToCourse,
  resolveUsersPostSuccessResponseValueToUserUIData as mapDataToUsers,
  toTuple,
} from "../../../parsers/resolve";
import { SagaAction } from "../../../sagas/types";
import {
  accountId as currentAccountId,
  currentClass,
  currentClassId,
  organizationId as currentOrganizationId,
} from "../../../selectors/selectors";
import { IClass, ICourse } from "../../types/common";
import { IClassManagementAndUsers } from "./";
import * as actions from "./";

const { api } = dependencies;

interface IFetchFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<IClassManagementAndUsers, Action<IClassManagementAndUsers>>;
}

type OnFailure = ActionFunction1<{ message: string }, Action<{ message: string }>>;

export function* resolveMembersAndManagersInfo(onFailure: OnFailure, classFound: IClass) {
  const accountId: string = yield select(currentAccountId);
  const organizationId: string = yield select(currentOrganizationId);
  const membersIds: string[] = classFound.members as string[];
  const managersIds: string[] = classFound.admins as string[];
  const membersAndManagersIds: string[] = [...membersIds, ...managersIds];
  const membersAndManagersTuple: Array<[ResolvableType, string]> = membersAndManagersIds.map(toTuple);

  const [resolvedInfoError, resolvedInfoResponse] = yield call(
    api.resolve.displayInfo.post,
    accountId,
    membersAndManagersTuple,
    organizationId
  );

  if (resolvedInfoError) {
    yield put(onFailure({ message: resolvedInfoError }));
    return;
  }
  const [membersInfo, managersInfo] = splitAt(length(membersIds), resolvedInfoResponse.data);
  const allMembers = zip(membersIds, membersInfo).map(([uuid, datum]) => mapDataToUsers(uuid, datum));
  const allManagers = zip(managersIds, managersInfo).map(([uuid, datum]) => mapDataToUsers(uuid, datum));
  return (yield { allMembers, allManagers }) as {
    allManagers: IUsersUIData[];
    allMembers: IUsersUIData[];
  };
}
function* getClass(onFailure: OnFailure) {
  const classId: string = yield select(currentClassId);
  const accountId: string = yield select(currentAccountId);
  const [fetchError, response]: AxiosInterceptedResult<Array<IResolveResponse<IClassResponse>>> = yield call(
    api.resolve.class.post,
    accountId,
    [classId]
  );

  if (fetchError || !response) {
    yield put(onFailure({ message: "Failed to find class" }));
    return;
  }

  const classesFound = zip([classId], response.data).map(([uuid, data]) => ({
    ...mapDataToClass(data),
    classId: uuid,
  }));

  if (!classesFound.length) {
    yield put(onFailure({ message: "Failure: Empty response (no classes)" }));
    return;
  }
  return (yield classesFound[0]) as IClass;
}

export const fetchClass: (fa: IFetchFurtherActions) => SagaFunction = fa =>
  function* _fetchClass(_: Action<void>): SagaAction {
    const classFound: IClass = yield getClass(fa.onFailure);
    if (!classFound) {
      return;
    }
    const {
      allMembers,
      allManagers,
    }: {
      allManagers: IUsersUIData[];
      allMembers: IUsersUIData[];
    } = yield resolveMembersAndManagersInfo(fa.onFailure, classFound);
    if (!allMembers || !allManagers) {
      return;
    }
    yield put(fa.onSuccess({ class: classFound, allMembers, allManagers }));
  };

interface IFetchInitialDataFurtherActions {
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
  onSuccess: ActionFunction1<actions.IInitialData, Action<actions.IInitialData>>;
}

export const fetchInitialData: (fa: IFetchInitialDataFurtherActions) => SagaFunction = fa =>
  function* _fetchInitialData(_: Action<void>): SagaAction {
    const accountId: string = yield select(currentAccountId);
    const organizationId: string = yield select(currentOrganizationId);
    const classFound: IClass | undefined = yield getClass(fa.onFailure);
    if (!classFound) {
      return;
    }
    const usersReponse: {
      allManagers: IUsersUIData[];
      allMembers: IUsersUIData[];
    } = yield resolveMembersAndManagersInfo(fa.onFailure, classFound);
    if (!usersReponse) {
      return;
    }
    const { allMembers, allManagers } = usersReponse;
    const [coursesError, response] = yield call(fetchAndResolveCourses, accountId, organizationId);

    if (coursesError) {
      return yield put(fa.onFailure(coursesError));
    }
    const {
      courseIdsResponse,
      coursesResponse,
    }: {
      courseIdsResponse: { data: string[] };
      coursesResponse: { data: any };
    } = response;
    const allCourses = zip(courseIdsResponse.data, coursesResponse.data).map(
      ([uuid, datum]): ICourse => ({
        ...mapDataToCourse(datum),
        courseId: uuid,
      })
    );
    return yield put(fa.onSuccess({ class: classFound, allCourses, allMembers, allManagers }));
  };

interface IPatchFurtherActions {
  onSuccess: ActionFunction1<string, Action<string>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export const patchClass: (fa: IPatchFurtherActions) => SagaFunction = fa =>
  function* _patchClass(action: Action<IClientPatch<IPatchAction>>): SagaAction {
    const current: IClass = yield select(currentClass);
    const accountId: string = yield select(currentAccountId);
    const patchPayload: IPatchPayload = yield call(patchPayloadCreator, current.versionId, action.payload);
    const [patchError] = yield call(api.classes.patchClass, accountId, current.classId, patchPayload);

    if (patchError) {
      yield put(fa.onFailure({ message: "Failed request to patch class" }));
      return;
    }

    yield put(actions.refreshClass());
    yield put(fa.onSuccess(current.classId));
  };

interface IDeleteFurtherActions {
  onSuccess: ActionFunction1<void, Action<void>>;
  onFailure: ActionFunction1<{ message: string }, Action<{ message: string }>>;
}

export const deleteClass: (fa: IDeleteFurtherActions) => SagaFunction = fa =>
  function* _deleteClass(_: Action<void>): SagaAction {
    const accountId: string = yield select(currentAccountId);
    const classToDelete: IClass = yield select(currentClass);
    const versionId = classToDelete.versionId;
    const deletePatch = yield call(modifyObjectDeletedPatch, versionId, accountId);
    const patchPayload = yield call(patchPayloadCreator, versionId, deletePatch);
    const [patchError] = yield call(api.classes.patchClass, accountId, classToDelete.classId, patchPayload);
    if (patchError) {
      return yield put(
        fa.onFailure({
          message: "Failed to patch class as deleted.",
        })
      );
    }

    yield put(fa.onSuccess());
    yield put(push("/classes"));
    yield put(Creators.loadClassAmount());
  };
